import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useEffect,
  useId,
  useMemo,
  useState
} from 'react'

import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { dialogInputStopPropagation } from '@/components/ConsoleDialog'

import { createOptionMap, formatOption } from '../SelectBox/SelectBox.utils'
import { ConsoleMultiSelectSkeleton } from './components/ConsoleMultiSelectSkeleton'
import { MultiSelectEndAdornment } from './components/MultiSelectEndAdornment'
import { NoOptionsMessage } from './components/NoOptionsMessage'
import {
  MULTI_SELECT_BOX_DEFAULT_MAX_LIST_HEIGHT,
  MULTI_SELECT_BOX_TEST_IDS
} from './MultiSelectBox.constants'
import type { MultiSelectBoxProps } from './MultiSelectBox.types'
import { MultiSelectValueChips } from './MultiSelectValueChips'

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const MultiSelectBox = <Option extends unknown>({
  defaultValue,
  value,
  getOptionId = formatOption,
  options,
  loading = false,
  testId = MULTI_SELECT_BOX_TEST_IDS.root,
  label,
  disabled = false,
  disabledInfo,
  error,
  excludeSelected = false,
  name,
  onBlur,
  onChange,
  onClose,
  onFocus,
  onOpen,
  noOptionsMessage = 'No options available',
  required = false,
  startAdornment,
  fullWidth = false,
  sx,
  maxListHeight = MULTI_SELECT_BOX_DEFAULT_MAX_LIST_HEIGHT,
  ...props
}: MultiSelectBoxProps<Option>) => {
  const {
    formatChipOption = getOptionId,
    formatListOption = props?.formatChipOption || getOptionId
  } = props
  const [internalValue, setInternalValue] = useState<string[]>([])
  const inputId = useId()
  const isControlled = value !== undefined && defaultValue === undefined
  const currentValue = isControlled ? (value ? value.map(getOptionId) : []) : internalValue

  useEffect(() => {
    if (!isControlled && defaultValue !== undefined) {
      setInternalValue(defaultValue.map(getOptionId))
    }
  }, [defaultValue, isControlled, getOptionId])

  const optionMap = useMemo(() => createOptionMap(options, getOptionId), [options, getOptionId])
  const filteredOptions = useMemo(() => {
    if (excludeSelected && currentValue.length > 0) {
      return options.filter((option) => !currentValue.includes(getOptionId(option)))
    }

    return options
  }, [options, excludeSelected, currentValue, getOptionId])

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    const selectedValues = Array.isArray(e.target.value) ? e.target.value : [e.target.value]

    setInternalValue(selectedValues)
    onChange?.(selectedValues.map((id) => optionMap.get(id)!).filter(Boolean))
  }

  const handleChipDelete = (e: MouseEvent<HTMLButtonElement>, optionId: string) => {
    e.preventDefault()
    e.stopPropagation()

    const newValue = currentValue.filter((id) => id !== optionId)
    setInternalValue(newValue)
    onChange?.(newValue.map((id) => optionMap.get(id)!).filter(Boolean))
  }

  const handleOpen = (_e: SyntheticEvent) => {
    onOpen?.()
  }

  const handleClose = (_e: SyntheticEvent) => {
    onClose?.()
  }

  const handleBlur = (_e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlur?.()
  }

  const handleFocus = (_e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFocus?.()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dialogInputStopPropagation.onKeyDown(e)
  }

  const hasValue = currentValue.length > 0

  return (
    <>
      <FormControl fullWidth={fullWidth} error={!!error} data-testid={testId}>
        <InputLabel htmlFor={inputId} aria-label={label}>
          {label}
        </InputLabel>
        <Select
          multiple
          id={inputId}
          name={name}
          required={required}
          disabled={disabled}
          error={!!error}
          label={label}
          aria-labelledby={inputId}
          value={currentValue}
          onChange={handleChange}
          onOpen={handleOpen}
          onClose={handleClose}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          startAdornment={startAdornment}
          endAdornment={<MultiSelectEndAdornment disabled={disabled} disabledInfo={disabledInfo} />}
          renderValue={(selected) => (
            <MultiSelectValueChips
              selectedIds={selected}
              optionMap={optionMap}
              formatChipOption={formatChipOption}
              handleDelete={handleChipDelete}
              disabled={disabled}
            />
          )}
          MenuProps={{
            MenuListProps: {
              sx: { maxHeight: maxListHeight }
            }
          }}
          sx={{
            '& .MuiSelect-select': {
              pb: hasValue ? 0.75 : undefined,
              pt: hasValue ? 0.84 : undefined
            },
            ...sx
          }}
          data-testid={MULTI_SELECT_BOX_TEST_IDS.input}
        >
          <ConsoleMultiSelectSkeleton loading={loading} />

          <NoOptionsMessage
            message={noOptionsMessage}
            show={!loading && filteredOptions.length === 0}
          />

          {!loading &&
            filteredOptions.map((option) => {
              const optionId = getOptionId(option)
              const isSelected = currentValue.includes(optionId)

              return (
                <MenuItem
                  key={optionId}
                  value={optionId}
                  data-testid={MULTI_SELECT_BOX_TEST_IDS.option(optionId)}
                >
                  {formatListOption(option, { selected: isSelected })}
                </MenuItem>
              )
            })}
        </Select>
      </FormControl>
      {!!error && <InputLabel error>{error}</InputLabel>}
    </>
  )
}
